/**
 * Utility functions for My Money section
 */

import { CATEGORY_CONFIG, DEFAULT_CATEGORY_CONFIG } from "@/constants/MyMoney";

/**
 * Format category name for display
 * Converts FOOD_AND_DRINK to "Food And Drink"
 */
export const formatCategoryName = (category: string): string => {
  if (category === "GENERAL_MERCHANDISE") return "Merchandise";
  return category
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Get category config with emoji, background color and formatted name
 */
export const getCategoryConfig = (
  category?: string[]
): { emoji: string; bgColor: string; color: string; name: string } => {
  const primary = category?.[0] || "GENERAL_MERCHANDISE";
  const config = CATEGORY_CONFIG[primary] || DEFAULT_CATEGORY_CONFIG;
  return { ...config, name: formatCategoryName(primary) };
};

/**
 * Format currency from cents to display string with 2 decimal places
 * @param cents - Amount in cents
 * @param showSign - Whether to show + or - sign
 */
export const formatCurrency = (cents: number, showSign = false): string => {
  const dollars = Math.abs(cents) / 100;
  const formatted = `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  if (showSign) {
    return cents >= 0 ? `+${formatted}` : `-${formatted}`;
  }
  return formatted;
};

/**
 * Format currency from cents without decimal places
 * @param cents - Amount in cents
 */
export const formatCurrencyWhole = (cents: number): string => {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Format currency from cents as a simple number (no $ sign)
 * Used for chart values
 * @param cents - Amount in cents
 */
export const formatCurrencyValue = (cents: number): number => {
  return cents / 100;
};

/**
 * Get category emoji from category key
 * @param category - Category key (e.g., "FOOD_AND_DRINK")
 */
export const getCategoryEmoji = (category: string): string => {
  return CATEGORY_CONFIG[category]?.emoji || DEFAULT_CATEGORY_CONFIG.emoji;
};

/**
 * Parse currency input string to cents
 * @param value - Input string (e.g., "123.45" or "$123.45")
 */
export const parseCurrencyInput = (value: string): number => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const dollars = parseFloat(cleaned) || 0;
  return Math.round(dollars * 100);
};

/**
 * Format cents to input value string (without $ sign)
 * @param cents - Amount in cents
 */
export const formatInputValue = (cents: number): string => {
  if (cents === 0) return "0.00";
  return (cents / 100).toFixed(2);
};

/**
 * Format cents to display string (with $ sign, no decimals)
 * @param cents - Amount in cents
 */
export const formatCentsToDisplay = (cents: number): string => {
  const dollars = cents / 100;
  return (
    "$" +
    dollars.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
};

/**
 * Parse display string (e.g., "$1,234") to cents
 * @param display - Display string with $ and commas
 */
export const parseDisplayToCents = (display: string): number => {
  const numStr = display.replace(/[$,]/g, "");
  const dollars = parseFloat(numStr) || 0;
  return Math.round(dollars * 100);
};

/**
 * Add commas to a number string for thousand separators
 * @param numStr - Number string (e.g., "1234.56")
 */
export const formatWithCommas = (numStr: string): string => {
  const parts = numStr.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

/**
 * Handle currency input change with $ prefix and comma formatting
 * Returns the formatted value to set in state
 * @param text - Raw input text
 */
export const handleCurrencyInputChange = (text: string): string => {
  if (!text.startsWith("$")) {
    text = "$" + text.replace(/\$/g, "");
  }

  const afterDollar = text.slice(1).replace(/,/g, "");

  let cleaned = "";
  let hasDot = false;

  for (const char of afterDollar) {
    if (char >= "0" && char <= "9") {
      cleaned += char;
    } else if (char === "." && !hasDot) {
      cleaned += char;
      hasDot = true;
    }
  }

  if (cleaned === "" || cleaned === ".") {
    return "$0";
  }

  if (cleaned.length > 1 && cleaned[0] === "0" && cleaned[1] !== ".") {
    cleaned = cleaned.replace(/^0+/, "") || "0";
  }

  return "$" + formatWithCommas(cleaned);
};
